import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
    userApi, 
    User, 
    CreateUserData, 
    UpdateUserData, 
    UsersListParams,
    UsersListResponse,
    Branch,
    City
} from '../../services/userApi';

interface UserState {
    users: User[];
    selectedUser: User | null;
    branches: Branch[];
    cities: City[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: {
        type: string;
        status: string;
        query: string;
    };
    loading: boolean;
    error: string | null;
}

const initialState: UserState = {
    users: [],
    selectedUser: null,
    branches: [],
    cities: [],
    pagination: null,
    filters: {
        type: '',
        status: '',
        query: ''
    },
    loading: false,
    error: null,
};

// Async thunks
export const fetchUsers = createAsyncThunk(
    'users/fetchUsers',
    async (params: UsersListParams = {}, { rejectWithValue }) => {
        try {
            const response = await userApi.getUsers(params);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch users');
        }
    }
);

export const fetchUser = createAsyncThunk(
    'users/fetchUser',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await userApi.getUser(id);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user');
        }
    }
);

export const createUser = createAsyncThunk(
    'users/createUser',
    async (userData: CreateUserData, { rejectWithValue }) => {
        try {
            const response = await userApi.createUser(userData);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to create user');
        }
    }
);

export const updateUser = createAsyncThunk(
    'users/updateUser',
    async ({ id, userData }: { id: number; userData: UpdateUserData }, { rejectWithValue }) => {
        try {
            const response = await userApi.updateUser(id, userData);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to update user');
        }
    }
);

export const deleteUser = createAsyncThunk(
    'users/deleteUser',
    async (id: number, { rejectWithValue }) => {
        try {
            await userApi.deleteUser(id);
            return id;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete user');
        }
    }
);

export const toggleUserStatus = createAsyncThunk(
    'users/toggleUserStatus',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await userApi.toggleUserStatus(id);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to toggle user status');
        }
    }
);

export const fetchBranches = createAsyncThunk(
    'users/fetchBranches',
    async (_, { rejectWithValue }) => {
        try {
            const response = await userApi.getBranches();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch branches');
        }
    }
);

export const fetchCities = createAsyncThunk(
    'users/fetchCities',
    async (_, { rejectWithValue }) => {
        try {
            const response = await userApi.getCities();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch cities');
        }
    }
);

const userSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSelectedUser: (state) => {
            state.selectedUser = null;
        },
        setFilters: (state, action: PayloadAction<Partial<UserState['filters']>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                type: '',
                status: '',
                query: ''
            };
        },
        resetState: (state) => {
            state.users = [];
            state.selectedUser = null;
            state.pagination = null;
            state.filters = {
                type: '',
                status: '',
                query: ''
            };
            state.loading = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch Users
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<UsersListResponse>) => {
                state.loading = false;
                state.users = action.payload.users;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Single User
        builder
            .addCase(fetchUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUser.fulfilled, (state, action: PayloadAction<User>) => {
                state.loading = false;
                state.selectedUser = action.payload;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Create User
        builder
            .addCase(createUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
                state.loading = false;
                state.users.unshift(action.payload);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Update User
        builder
            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUser.fulfilled, (state, action: PayloadAction<User>) => {
                state.loading = false;
                const index = state.users.findIndex(user => user.id === action.payload.id);
                if (index !== -1) {
                    state.users[index] = action.payload;
                }
                if (state.selectedUser?.id === action.payload.id) {
                    state.selectedUser = action.payload;
                }
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Delete User
        builder
            .addCase(deleteUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteUser.fulfilled, (state, action: PayloadAction<number>) => {
                state.loading = false;
                state.users = state.users.filter(user => user.id !== action.payload);
                if (state.selectedUser?.id === action.payload) {
                    state.selectedUser = null;
                }
                if (state.pagination) {
                    state.pagination.total -= 1;
                }
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Toggle User Status
        builder
            .addCase(toggleUserStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleUserStatus.fulfilled, (state, action: PayloadAction<{ id: number; name: string; status: number }>) => {
                state.loading = false;
                const index = state.users.findIndex(user => user.id === action.payload.id);
                if (index !== -1) {
                    state.users[index].status = action.payload.status;
                }
                if (state.selectedUser?.id === action.payload.id) {
                    state.selectedUser.status = action.payload.status;
                }
            })
            .addCase(toggleUserStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Branches
        builder
            .addCase(fetchBranches.fulfilled, (state, action: PayloadAction<Branch[]>) => {
                state.branches = action.payload;
            });

        // Fetch Cities
        builder
            .addCase(fetchCities.fulfilled, (state, action: PayloadAction<City[]>) => {
                state.cities = action.payload;
            });
    },
});

export const { 
    clearError, 
    clearSelectedUser, 
    setFilters, 
    clearFilters, 
    resetState 
} = userSlice.actions;

export default userSlice.reducer; 